import React, { useEffect, useState } from "react";
import { Modal } from "@shared/controls/Modal";
import { toast } from "react-toastify";
import customFetch from "@services/axio";
import { initiateTransactionBankTransfer } from "@services/krent.service";
import { formatCurrency } from "@utils/helpers";
import PaymentIcon from "../assets/payment-icon.svg";
import "./PaymentMethodModal.css";

export interface StartedRentBooking {
  transactionId: string;
  referenceId: string;
  checkout?: { authorization_url?: string };
  bankDetails?: { bankName: string; accountNumber: string; accountName: string };
}

interface PaymentMethodModalProps {
  show: boolean;
  onClose: () => void;
  bookingId: string;
  totalAmount: number;
  currency?: string;
  referenceId?: string;
  startRentBooking?: (paymentMethod: "paystack" | "manual_bank_transfer") => Promise<StartedRentBooking>;
}

const errorMessage = (err: any, fallback: string) =>
  err?.response?.data?.errors?.[0]?.message ||
  err?.response?.data?.message ||
  err?.errors?.[0]?.message ||
  err?.message ||
  fallback;

const PaymentMethodModal: React.FC<PaymentMethodModalProps> = ({
  show,
  onClose,
  bookingId,
  totalAmount,
  currency = "₦",
  referenceId = "",
  startRentBooking,
}) => {
  const [activeBookingId, setActiveBookingId] = useState(bookingId);
  const [activeReferenceId, setActiveReferenceId] = useState(referenceId);
  const [bankTransferStarted, setBankTransferStarted] = useState(false);
  useEffect(() => setActiveBookingId(bookingId), [bookingId]);
  useEffect(() => setActiveReferenceId(referenceId), [referenceId]);
  const createsBooking = !!startRentBooking && !activeBookingId;
  const [selectedMethod, setSelectedMethod] = useState<
    "bank" | "paystack" | null
  >(null);
  const [bankDetails, setBankDetails] = useState<{
    bankName: string;
    accountNumber: string;
    accountName: string;
  } | null>(null);
  const [bankDetailsLoading, setBankDetailsLoading] = useState(false);
  const [paystackLoading, setPaystackLoading] = useState(false);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptRemarks, setReceiptRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    // Don't allow closing mid-submit
    if (submitting || paystackLoading) return;
    setSelectedMethod(null);
    setReceiptFile(null);
    setReceiptRemarks("");
    setBankDetails(null);
    setBankTransferStarted(false);
    onClose();
  };

  const fetchBankDetails = async () => {
    if (bankDetails) return;
    setBankDetailsLoading(true);
    try {
      const res = await customFetch.get("/transactions/account-details/");
      const d = res?.data?.data || res?.data;
      setBankDetails({
        bankName: d?.bankName || d?.bank_name || d?.bank || "",
        accountNumber: d?.accountNumber || d?.account_number || "",
        accountName: d?.accountName || d?.account_name || "",
      });
    } catch {
      toast.error("Could not fetch bank details. Please try again.");
    } finally {
      setBankDetailsLoading(false);
    }
  };

  const chooseBankTransfer = async () => {
    setSelectedMethod("bank");
    if (!createsBooking) {
      fetchBankDetails();
      return;
    }
    if (bankDetailsLoading) return;
    setBankDetailsLoading(true);
    try {
      const started = await startRentBooking!("manual_bank_transfer");
      setActiveBookingId(started.transactionId);
      setActiveReferenceId(started.referenceId);
      setBankTransferStarted(true);
      if (started.bankDetails) setBankDetails(started.bankDetails);
      else await fetchBankDetails();
    } catch (err: any) {
      setSelectedMethod(null);
      toast.error(errorMessage(err, "Could not start your bank transfer. Please try again."));
    } finally {
      setBankDetailsLoading(false);
    }
  };

  const initiatePaystack = async () => {
    if (paystackLoading) return;
    if (!activeBookingId && !createsBooking) {
      toast.error(
        "Payment details are unavailable. Please open My Bookings to continue payment.",
      );
      return;
    }
    setPaystackLoading(true);
    let redirecting = false;
    try {
      if (createsBooking) {
        const started = await startRentBooking!("paystack");
        setActiveBookingId(started.transactionId);
        if (started.checkout?.authorization_url) {
          window.location.href = started.checkout.authorization_url;
          redirecting = true;
        } else {
          toast.error("Your booking was created, but the payment link is unavailable. Open My Bookings to continue payment.");
        }
        return;
      }
      const res = await customFetch.post(
        `/transactions/${activeBookingId}/initiate-payment`,
        {},
      );
      const url =
        res?.data?.data?.authorization_url ||
        res?.data?.authorization_url ||
        res?.data?.data?.url ||
        res?.data?.url;
      if (url) {
        // Full redirect so Paystack's callback URL can return the user
        // back to /booking/confirm?trxref=REF (handled by BookingConfirm)
        window.location.href = url;
        redirecting = true;
      } else {
        toast.error("Could not retrieve payment link. Please try again.");
      }
    } catch (err: any) {
      toast.error(errorMessage(err, "Failed to initiate payment. Please try again."));
    } finally {
      if (!redirecting) setPaystackLoading(false);
    }
  };

  /**
   * Two-step bank transfer submission:
   * 1. POST /transactions/{id}/initiate-bank-transfer
   * 2. POST /transactions/{id}/upload-bank-transfer-proof
   */
  const submitReceipt = async () => {
    if (submitting) return;
    if (!activeBookingId) {
      toast.error(
        "Payment details are unavailable. Please open My Bookings to submit your receipt.",
      );
      return;
    }
    if (!receiptFile) {
      toast.error("Please upload your payment receipt first.");
      return;
    }
    setSubmitting(true);
    try {
      if (!bankTransferStarted) await initiateTransactionBankTransfer(activeBookingId);

      const formData = new FormData();
      formData.append("paymentProof", receiptFile);
      if (receiptRemarks.trim()) {
        formData.append("paymentProofRenterComment", receiptRemarks.trim());
      }
      await customFetch.post(
        `/transactions/${activeBookingId}/upload-bank-transfer-proof`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      // Redirect to confirmation page instead of showing inline modal
      onClose();
      const redirectUrl = activeReferenceId
        ? `/booking/confirm?trxref=${encodeURIComponent(activeReferenceId)}&reference=${encodeURIComponent(activeReferenceId)}`
        : "/my-bookings";
      window.location.href = redirectUrl;
    } catch (err: any) {
      toast.error(errorMessage(err, "Failed to submit receipt. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Selection / bank details screen ────────────────────────────────────
  return (
    <Modal
      show={show}
      onClose={handleClose}
      size={620}
      className={`payment-method-modal${paystackLoading ? " payment-method-modal--connecting" : ""}`}
      dismissible={!paystackLoading}
      backdrop="static"
    >
      {paystackLoading ? (
        <div
          className="paystack-connecting font-onest"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <svg
            className="paystack-connecting__spinner"
            viewBox="0 0 56 56"
            aria-hidden="true"
          >
            <circle cx="28" cy="28" r="24" fill="#edf9f6" />
            <circle
              cx="28"
              cy="28"
              r="24"
              fill="none"
              stroke="#47bba9"
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray="125 26"
            />
          </svg>
          <h4>Connects to Paystack</h4>
          <p className="paystack-connecting__subtitle">
            Secure Checkout via Paystack
          </p>
          <div className="paystack-connecting__dots" aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <p className="paystack-connecting__hint">
            This should only take a moment
          </p>
        </div>
      ) : (
        <div className="payment-method-content text-center">
          {/* Payment icon */}
          <div className="payment-method-icon mx-auto d-flex align-items-center justify-content-center rounded-circle">
            <img
              src={PaymentIcon}
              alt="Payment"
              style={{ width: "44px", height: "44px" }}
            />
          </div>

          <h5 className="payment-method-title fw-bold font-onest">
            Choose payment method
          </h5>
          <p className="payment-method-description font-onest">
            Select how you would like to complete your payment of{" "}
            <span className="payment-method-amount">
              {currency === "NGN" ? "₦" : currency}
              {formatCurrency(totalAmount)}
            </span>
            .
          </p>

          {/* Bank Transfer option */}
          <div
            className={`payment-method-option text-start${selectedMethod === "bank" ? " payment-method-option--selected" : ""}`}
            aria-pressed={selectedMethod === "bank"}
            role="button"
            tabIndex={0}
            onClick={chooseBankTransfer}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                chooseBankTransfer();
              }
            }}
          >
            <div
              className="d-flex align-items-center justify-content-center flex-shrink-0"
              style={{ width: "52px", height: "52px" }}
            >
              <svg
                width="36"
                height="36"
                viewBox="0 0 36 36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g
                  stroke="#47BBA9"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M2 12L18 2l16 10v2H2zM5 30V14m9 16V14m8 16V14m9 16V14M5 30h26l3 4H2z" />
                  <circle cx="18" cy="8" r="1" fill="#47BBA9" stroke="none" />
                </g>
              </svg>
            </div>
            <div>
              <p
                className="fw-bold font-onest mb-1"
                style={{ fontSize: "15px", color: "#1a1a1a" }}
              >
                Bank Transfer
              </p>
              <p
                className="font-onest mb-0"
                style={{
                  fontSize: "14px",
                  color: "#7a7a7a",
                  lineHeight: "1.5",
                }}
              >
                Transfer to our account and upload your receipt.
              </p>
            </div>
          </div>

          {/* Paystack option */}
          <div
          className={`payment-method-option text-start${selectedMethod === "paystack" ? " payment-method-option--selected" : ""}`}
          aria-pressed={selectedMethod === "paystack"}
          role="button"
          tabIndex={0}
          onClick={() => setSelectedMethod("paystack")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setSelectedMethod("paystack");
            }
          }}
        >
          <div
            className="d-flex align-items-center justify-content-center flex-shrink-0"
            style={{ width: "52px", height: "52px" }}
          >
            <svg
              width="32"
              height="26"
              viewBox="0 0 32 26"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect y="0" width="32" height="6" rx="2" fill="#0BA4DB" />
              <rect y="8" width="26" height="6" rx="2" fill="#0BA4DB" />
              <rect y="16" width="20" height="6" rx="2" fill="#0BA4DB" />
            </svg>
          </div>
          <div>
            <p
              className="fw-bold font-onest mb-1"
              style={{ fontSize: "15px", color: "#1a1a1a" }}
            >
              Paystack
            </p>
            <p
              className="font-onest mb-0"
              style={{ fontSize: "14px", color: "#7a7a7a", lineHeight: "1.5" }}
            >
              Fast and secure checkout with Debit Card, USSD, or Bank.
            </p>
          </div>
        </div>

          {/* Paystack notice */}
          {selectedMethod === "paystack" && (
            <div
              className="d-flex align-items-start gap-2 rounded-3 p-3 mb-4 text-start"
              style={{ background: "#FFFBEA", border: "1px solid #F5E6A3" }}
            >
              <span style={{ fontSize: "16px", flexShrink: 0 }}>ℹ️</span>
              <p className="mb-0 fs-13 font-onest" style={{ color: "#7a6200" }}>
                Paystack will deduct a small percentage fee (similar to a tax)
                on the Paystack payment screen. The amount shown above is your
                rent total before that fee.
              </p>
            </div>
          )}

          {/* Bank Transfer details + receipt upload */}
          {selectedMethod === "bank" && (
            <div className="text-start mb-4">
              {bankDetailsLoading ? (
                <div className="text-center py-3">
                  <span
                    className="spinner-border spinner-border-sm text-primary"
                    role="status"
                  />
                </div>
              ) : bankDetails ? (
                <>
                  <div
                    className="rounded-3 p-3 mb-3"
                    style={{
                      border: "1px solid #dee2e6",
                      background: "#fafffe",
                    }}
                  >
                    <p className="fw-bold font-onest mb-3 fs-14">
                      Transfer details
                    </p>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="font-onest fs-13 text-muted">Bank</span>
                      <span className="font-onest fs-13 fw-semibold">
                        {bankDetails.bankName}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="font-onest fs-13 text-muted">
                        Account number
                      </span>
                      <div className="d-flex align-items-center gap-2">
                        <span className="font-onest fs-13 fw-semibold">
                          {bankDetails.accountNumber}
                        </span>
                        <button
                          type="button"
                          className="btn p-0 border-0"
                          title="Copy"
                          onClick={() => {
                            navigator.clipboard.writeText(
                              bankDetails.accountNumber,
                            );
                            toast.success("Copied!");
                          }}
                        >
                          <i
                            className="fa-regular fa-copy text-primary"
                            style={{ fontSize: "14px" }}
                          />
                        </button>
                      </div>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span className="font-onest fs-13 text-muted">
                        Account name
                      </span>
                      <span className="font-onest fs-13 fw-semibold">
                        {bankDetails.accountName}
                      </span>
                    </div>
                    <p
                      className="font-onest fs-13 mb-0"
                      style={{ color: "#374151" }}
                    >
                      Transfer the exact total of{" "}
                      <strong>NGN{formatCurrency(totalAmount)}</strong>, then
                      upload your receipt below.
                    </p>
                  </div>

                  {/* Receipt upload */}
                  <div
                    className="rounded-3 p-3 mb-3 text-center"
                    style={{
                      border: "1.5px dashed #dee2e6",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      document.getElementById("receiptFileInputModal")?.click()
                    }
                  >
                    <input
                      id="receiptFileInputModal"
                      type="file"
                      accept="image/*,.pdf"
                      style={{ display: "none" }}
                      onChange={(e) =>
                        setReceiptFile(e.target.files?.[0] || null)
                      }
                    />
                    {receiptFile ? (
                      <div className="d-flex align-items-center justify-content-center gap-2">
                        <i className="fa-regular fa-file text-primary fs-18" />
                        <span className="font-onest fs-13 fw-semibold text-primary">
                          {receiptFile.name}
                        </span>
                      </div>
                    ) : (
                      <>
                        <i
                          className="fa-solid fa-cloud-arrow-up text-primary mb-2"
                          style={{ fontSize: "28px" }}
                        />
                        <p className="fw-semibold font-onest mb-0 fs-14">
                          Upload payment receipt
                        </p>
                        <p
                          className="font-onest mb-0"
                          style={{ fontSize: "12px", color: "#6b7280" }}
                        >
                          Image or PDF · required for transfer payments
                        </p>
                      </>
                    )}
                  </div>

                  <label
                    className="font-onest fs-13 mb-1 d-block"
                    style={{ color: "#374151" }}
                  >
                    Remarks (optional)
                  </label>
                  <input
                    type="text"
                    className="form-control rounded-3 mb-4 fs-14"
                    placeholder="e.g. payment reference"
                    value={receiptRemarks}
                    onChange={(e) => setReceiptRemarks(e.target.value)}
                  />
                </>
              ) : null}
            </div>
          )}

          {/* CTA — Paystack */}
          {selectedMethod === "paystack" && (
            <button
              type="button"
              className="btn py-3 w-100 fw-semibold font-onest rounded-4"
              style={{
                background: "#47BBA9",
                color: "#fff",
                border: "none",
                fontSize: "16px",
              }}
              disabled={paystackLoading}
              onClick={initiatePaystack}
            >
              {paystackLoading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                "Proceed to Payment"
              )}
            </button>
          )}

          {/* CTA — Bank Transfer */}
          {selectedMethod === "bank" && !bankDetailsLoading && bankDetails && (
            <button
              type="button"
              className="btn py-3 w-100 fw-semibold font-onest rounded-4"
              style={{
                background: "#47BBA9",
                color: "#fff",
                border: "none",
                fontSize: "16px",
              }}
              disabled={!receiptFile || submitting}
              onClick={submitReceipt}
            >
              {submitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                  Submitting…
                </>
              ) : (
                "Submit Receipt"
              )}
            </button>
          )}
        </div>
      )}
    </Modal>
  );
};

export default PaymentMethodModal;
